import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kApplyTemplateApiPath } from '@/constants/constants-api-paths'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { z } from 'zod'

export type ApplyTemplateToDraftResponse = {
  docxPath: string
  // Server time the template was applied (the persisted record's `last_applied_at`). Stored locally
  // so the freshness check compares same-clock server timestamps. Null if an older backend omits it.
  lastAppliedAt: number | null
}

const ApplyTemplateToDraftApiResponseSchema = z.object({
  template_applied_docx_gcs_path: z.string().min(1),
  last_applied_at: z.union([z.string(), z.number()]).nullable().optional(),
})

function formatErrorDetail(data: unknown): string {
  const responseData = data as { detail?: unknown } | undefined
  const detail = responseData?.detail ?? data
  if (typeof detail === 'string') return detail
  if (detail == null) return ''
  return JSON.stringify(detail)
}

/**
 * Apply a template to an artifact version. The backend renders the version's markdown with the
 * template, persists the applied record for that version, and returns the rendered docx path plus
 * the server apply time to download or save.
 */
export async function applyTemplateToDraft(body: {
  artifactVersionId: string
  matterId: string
  templateUserFileId: string
}): Promise<ApplyTemplateToDraftResponse> {
  const sentry_transaction_id = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error(kErrorCodeAuthError)

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kApplyTemplateApiPath}`
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${token}`,
        'X-Transaction-Id': sentry_transaction_id,
      },
      body: JSON.stringify({
        artifact_version_id: body.artifactVersionId,
        matter_id: body.matterId,
        template_user_file_id: body.templateUserFileId,
      }),
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const errorData = parseError ? {} : data
      const detail = formatErrorDetail(errorData)
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error(`Apply template failed - status: ${response.status}, detail: ${detail}`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            requestBody: body,
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
          },
        })
      })
      throw new ApiError(response.status, `Failed to apply template: ${response.status}`)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error('Apply template response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            requestBody: body,
            parseError: formatUnknownError(parseError),
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid apply template response JSON')
    }

    const parsed = ApplyTemplateToDraftApiResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error('Apply template response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            requestBody: body,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid apply template response shape', parsed.error.issues)
    }

    // BE returns a datetime string (or ms); normalize to epoch ms, null if absent/unparseable.
    let lastAppliedAt: number | null = null
    if (typeof parsed.data.last_applied_at === 'number') lastAppliedAt = parsed.data.last_applied_at
    else if (typeof parsed.data.last_applied_at === 'string') {
      const ms = Date.parse(parsed.data.last_applied_at)
      if (!Number.isNaN(ms)) lastAppliedAt = ms
    }

    return { docxPath: parsed.data.template_applied_docx_gcs_path, lastAppliedAt }
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(error)
      })
    }
    throw error
  }
}
